import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Eye, FlaskConical, TrendingUp, Wrench, Mail, Copy, Check, KeyRound, FileText, Ruler, Lightbulb, ArrowRight, Building2 } from "lucide-react";
import Logo from "../components/Logo";
import { api } from "../software/api";
import { setViewAs } from "../access";
import { OUTREACH } from "../components/shop/OutreachEmail";

function InviteBox({ title, to, subject, body, testid }) {
  const [copied, setCopied] = useState(false);
  const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const copy = async () => {
    const full = `To: ${to}\nSubject: ${subject}\n\n${body}`;
    try { await navigator.clipboard.writeText(full); }
    catch (e) { const ta = document.createElement("textarea"); ta.value = full; document.body.appendChild(ta); ta.select(); try { document.execCommand("copy"); } catch (err) {} document.body.removeChild(ta); }
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className="font-display font-bold flex items-center gap-2"><Mail className="h-4 w-4 text-sky-600" /> {title}</p>
      <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4 max-h-56 overflow-auto">
        {to && (<><p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">To</p><p className="text-sm text-slate-800 font-medium">{to}</p></>)}
        <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400 mt-2">Subject</p>
        <p className="text-sm text-slate-800 font-medium">{subject}</p>
        <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400 mt-2">Body</p>
        <pre className="whitespace-pre-wrap font-sans text-[13px] text-slate-700 leading-relaxed mt-1" data-testid={`${testid}-body`}>{body}</pre>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <a href={mailto} data-testid={`${testid}-mailto`} className="inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold px-4 py-2 transition-colors"><Mail className="h-4 w-4" /> Open in email app</a>
        <button onClick={copy} data-testid={`${testid}-copy`} className={`inline-flex items-center gap-2 rounded-full text-sm font-semibold px-4 py-2 transition-colors ${copied ? "bg-emerald-600 text-white" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}</button>
      </div>
    </div>
  );
}

export default function AccessCenter() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState({ investor: "", manufacturer: "" });
  const origin = useMemo(() => (typeof window !== "undefined" ? window.location.origin : ""), []);

  useEffect(() => { api("GET", "/access/codes").then(setCodes).catch(() => {}); }, []);

  const preview = (aud, to) => { setViewAs(aud); navigate(to); };

  const investorBody = useMemo(() => {
    const link = `${origin}/proposal`;
    let b = OUTREACH.biolab.body.replace("[PROPOSAL_LINK]", link);
    b = b.replace(
      "(You'll be asked to accept a short confidentiality agreement first — your access is logged for both our protection.)",
      `How to sign in:\n• Open the link above\n• Enter your name and your email: gene.lawless@biolabinc.com\n• Access password: ${codes.investor || "kevingene"}\n\n(You'll be asked to accept a short confidentiality agreement first — your access is logged for both our protection.)`
    );
    return b;
  }, [origin, codes.investor]);

  const mfrBody = useMemo(() => {
    const link = `${origin}/manufacturer`;
    return `${OUTREACH.manufacturer.body}\n\nWhen you're ready, the full build pack is here:\n${link}\n• Access password: ${codes.manufacturer || "kevinbuild"}\n(You'll accept a short NDA on entry.)`;
  }, [origin, codes.manufacturer]);

  const LENSES = [
    { key: "admin", icon: ShieldCheck, name: "Admin — you", tone: "bg-slate-900 text-white", desc: "Everything: development, NDA log, proposals, emails, Patent/IP ideas and the manufacturer build designs.", action: null },
    { key: "investor", icon: TrendingUp, name: "Investor — what Gene sees", tone: "bg-sky-600 text-white", desc: "The proposal: vision, live software, the numbers and the strategic-alliance ask. No patent ideas, no build/BOM drawings.", action: () => preview("investor", "/proposal") },
    { key: "manufacturer", icon: Wrench, name: "Manufacturer — build partner", tone: "bg-emerald-600 text-white", desc: "The build pack: spec, interior, RFQ and quoting bands. No patent ideas, no investor equity ask.", action: () => preview("manufacturer", "/manufacturer") },
  ];

  const DOCS = [
    { to: "/shop/ip", icon: Lightbulb, name: "Patent / IP ideas", note: "Admin only" },
    { to: "/shop/build", icon: Ruler, name: "Build designs" },
    { to: "/shop/brief", icon: FileText, name: "Manufacturing brief / RFQ" },
    { to: "/proposal", icon: TrendingUp, name: "Investor proposal" },
    { to: "/manufacturer", icon: Wrench, name: "Manufacturer view" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFCFD] text-slate-900" data-testid="access-center-page">
      <header className="sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">Admin · Access &amp; Views</span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 lg:px-8 py-12">
        <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-mono2 text-sky-700 mb-5"><Eye className="h-3.5 w-3.5" /> Three access levels</span>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold">Access &amp; Views control</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">Preview exactly what each audience sees, share the right password, and reach every confidential document — all from here.</p>

        {/* Lenses */}
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {LENSES.map((l) => { const Icon = l.icon; return (
            <div key={l.key} className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col">
              <div className={`p-5 ${l.tone}`}><Icon className="h-6 w-6" /><h2 className="font-display text-lg font-bold mt-2">{l.name}</h2></div>
              <div className="p-5 flex-1 flex flex-col">
                <p className="text-sm text-slate-600 flex-1">{l.desc}</p>
                {l.action ? (
                  <button data-testid={`view-as-${l.key}`} onClick={l.action} className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2.5 transition-colors"><Eye className="h-4 w-4" /> Preview this view</button>
                ) : (
                  <span className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-slate-100 text-slate-500 text-sm font-semibold px-4 py-2.5">You are here</span>
                )}
              </div>
            </div>
          ); })}
        </div>

        {/* Passwords */}
        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
          <p className="font-display font-bold flex items-center gap-2"><KeyRound className="h-4 w-4 text-sky-600" /> Access passwords to share</p>
          <p className="text-sm text-slate-500 mt-1">Give the matching password with each invite — it decides which view the visitor lands in.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-sky-50 border border-sky-100 p-4"><p className="font-mono2 text-[10px] uppercase tracking-widest text-sky-700">Investor password</p><p className="font-display text-xl font-extrabold text-slate-900 mt-1" data-testid="code-investor">{codes.investor || "—"}</p></div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4"><p className="font-mono2 text-[10px] uppercase tracking-widest text-emerald-700">Manufacturer password</p><p className="font-display text-xl font-extrabold text-slate-900 mt-1" data-testid="code-manufacturer">{codes.manufacturer || "—"}</p></div>
          </div>
        </div>

        {/* Invites */}
        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          <InviteBox testid="invite-investor" title="Send to Gene (investor)" to="gene.lawless@biolabinc.com" subject={OUTREACH.biolab.subject} body={investorBody} />
          <InviteBox testid="invite-manufacturer" title="Send to a manufacturer" to="" subject={OUTREACH.manufacturer.subject} body={mfrBody} />
        </div>

        {/* Confidential docs */}
        <div className="mt-10">
          <p className="font-display font-bold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sky-600" /> Confidential documents (admin)</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {DOCS.map((d) => { const Icon = d.icon; return (
              <Link key={d.to} to={d.to} data-testid={`doc-${d.to.replace(/\//g, "-")}`} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-sky-300 hover:text-sky-700 transition-colors">
                <Icon className="h-4 w-4" /> {d.name}{d.note && <span className="text-[10px] font-mono2 text-slate-400 ml-1">{d.note}</span>} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ); })}
          </div>
        </div>
      </main>
    </div>
  );
}
